import { keepPreviousData } from '@tanstack/react-query';
import React, { FC, useEffect, useMemo } from 'react';
import {
  FormSelect,
  useListClassifiersQuery,
  filterOption,
  useGetClassifierQuery,
  FormField,
  FormInput,
  Padding,
  CODE_MAX_LENGTH,
  NAME_MAX_LENGTH,
} from '@atlas-frontend/sdk';
import { DefaultOptionType } from 'rc-select/lib/Select';
import { useWatch } from 'react-hook-form';
import translations from './ClassifierMappingFields.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { useClassifierMappingContext } from './ClassifierMappingContext';
import { Flex } from 'antd';

const { addTranslations, useTranslation } = i18nNsFactory(
  'classifier-mapping-fields',
);
addTranslations(translations);

export const ClassifierMappingFields = ({
  readOnly = false,
}: {
  readOnly?: boolean;
}) => {
  const { t } = useTranslation();
  const { data } = useListClassifiersQuery(
    {
      page: 0,
      size: 99999,
      hasCategories: true,
    },
    {
      placeholderData: keepPreviousData,
    },
  );

  const options: DefaultOptionType[] | undefined = useMemo(
    () =>
      data?.content.map((classifier) => ({
        id: classifier.id,
        label: `${classifier.code} - ${classifier.name}`,
        value: classifier.id,
      })),
    [data],
  );

  const inputProps = readOnly
    ? { className: 'ant-input-disabled', readOnly }
    : {};

  return (
    <Padding>
      <Flex vertical gap="large">
        <FormField name="code" label={t('field.code.label')} required={true}>
          <FormInput
            {...inputProps}
            readOnly={readOnly}
            name="code"
            showCount
            maxLength={CODE_MAX_LENGTH}
          />
        </FormField>
        <FormField name="name" label={t('field.name.label')} required={true}>
          <FormInput
            {...inputProps}
            readOnly={readOnly}
            name="name"
            showCount
            maxLength={NAME_MAX_LENGTH}
          />
        </FormField>
        <FormField
          name="classifierIdFrom"
          label={t('field.classifierFrom.label')}
          required={true}
        >
          <ClassifierSelect from options={options} readOnly={readOnly} />
        </FormField>
        <FormField
          name="classifierIdTo"
          label={t('field.classifierTo.label')}
          required={true}
        >
          <ClassifierSelect to options={options} readOnly={readOnly} />
        </FormField>
        <FormField name="description" label={t('field.description.label')}>
          <FormInput name="description" rows={3} maxLength={2000} />
        </FormField>
      </Flex>
    </Padding>
  );
};

export type ClassifierMappingFieldsProps = {
  options?: DefaultOptionType[];
  readOnly: boolean;
} & (
  | {
      from: true;
      to?: never;
    }
  | {
      from?: never;
      to: true;
    }
);

export const ClassifierSelect: FC<ClassifierMappingFieldsProps> = (props) => {
  const { from, options = [], readOnly } = props;
  const name = from ? 'classifierIdFrom' : 'classifierIdTo';
  const { setClassifierTo, setClassifierFrom } = useClassifierMappingContext();

  const selectedId = useWatch({ name });

  const { data } = useGetClassifierQuery(selectedId, {
    enabled: !!selectedId,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (!data) {
      return;
    }
    if (from) {
      setClassifierFrom(data);
    } else {
      setClassifierTo(data);
    }
  }, [data, from, setClassifierTo, setClassifierFrom]);

  const mappingCount = useWatch({
    name: 'categoryMappings',
  })?.length;

  return (
    <FormSelect
      disabled={!!mappingCount || readOnly}
      filterOption={filterOption}
      name={name}
      options={options}
      showSearch
    />
  );
};
